/**
 * Ce qu'un passage sur un exercice raconte d'un coup d'œil.
 *
 * ⚠️ **Un fichier à part** : `react-refresh` interdit à un module de composants
 * d'exporter autre chose — la règle qui a déjà sorti `setLabels.ts` et
 * `setValues.ts` des écrans de séance. Une fonction pure lue par le graphique
 * **et** par la fiche qui décide de l'afficher n'a rien à faire dans l'un des
 * deux.
 */

import { epley } from '../../../../shared/oneRepMax';
import type { ActualSetDTO, LastPassageDTO } from '../../../../shared/types';

/**
 * La charge d'un passage : sa **série la plus lourde**.
 *
 * 🔴 **Trois agrégats se défendaient — la plus lourde, la moyenne, la
 * dernière — et il fallait en choisir un**, parce qu'une courbe qui les
 * mélangerait ne voudrait rien dire. La plus lourde est la seule qui réponde à
 * la question qu'on se pose devant la barre (« est-ce que je monte ? ») : une
 * moyenne baisse dès qu'on ajoute une série légère en fin de séance, et la
 * dernière série est souvent la plus fatiguée.
 *
 * ⚠️ **Calculée à la lecture, jamais stockée** (§6.1 : aucune statistique
 * matérialisée). Corriger une charge d'il y a trois semaines redessine la
 * courbe immédiatement, sans invalidation à déclencher.
 *
 * ⚠️ **`null`, jamais zéro, quand aucune série n'a de charge** — poids du
 * corps, gainage. « Zéro kilo » et « pas de charge » ne sont pas la même chose,
 * et une courbe qui plonge à zéro raconterait une régression qui n'a pas eu
 * lieu.
 */
export function topWeightOf(passage: LastPassageDTO): number | null {
  const charges = passage.sets
    .map((set) => set.weightKg)
    .filter((kg): kg is number => kg !== null);
  return charges.length === 0 ? null : Math.max(...charges);
}

/**
 * Le volume d'un passage : **tout ce qui a été fait**, additionné.
 *
 * 🔴 **Demandé après usage** *(29/08/2026)* : « le graphique doit aussi
 * contenir le volume de reps ». La charge seule ment par omission — monter de
 * 60 à 62,5 kg en passant de 4×10 à 3×6, c'est **descendre**. Les deux courbes
 * ensemble racontent l'entraînement ; l'une sans l'autre en raconte la moitié.
 *
 * ⚠️ **L'unité suit la nature de l'exercice**, comme partout depuis la
 * migration `0010` : des répétitions pour un exercice qui s'en compte, des
 * secondes pour un gainage. On n'additionne jamais les deux — ce serait le
 * genre de nombre qui a l'air d'en être un.
 *
 * ⚠️ **Le tonnage (Σ reps × charge) n'est PAS retenu ici**, alors que le §6.2
 * le range parmi les statistiques. Il y a sa place — série temporelle à
 * exercice constant — mais il mélange les deux grandeurs que ce graphique
 * cherche justement à séparer, et il « ne se lit jamais comparé entre
 * exercices ». Ce sera l'affaire de l'étape 9.
 */
export function volumeOf(passage: LastPassageDTO): { value: number; unit: 'reps' | 's' } | null {
  const reps = passage.sets
    .map((set) => set.reps)
    .filter((value): value is number => value !== null);
  if (reps.length > 0) {
    return { value: reps.reduce((total, value) => total + value, 0), unit: 'reps' };
  }

  const secondes = passage.sets
    .map((set) => set.durationSeconds)
    .filter((value): value is number => value !== null);
  if (secondes.length > 0) {
    return { value: secondes.reduce((total, value) => total + value, 0), unit: 's' };
  }

  return null;
}

/* ==========================================================================
 * Les cinq métriques de la courbe d'historique *(06/09/2026)*
 * ======================================================================= */

/**
 * 🔴 **Ce qui compte comme une série de travail — et c'est le MÊME filtre que
 * le tableau de bord** (`server/src/services/stats.ts`, étape 9).
 *
 * L'échauffement sort, et il n'y a pas à discuter : « un échauffement ne fait
 * pas travailler, c'est sa définition » — la règle est déjà celle de
 * `muscleVolume()` et celle du volume par muscle. Le laisser entrer gonflerait
 * le total de répétitions et le volume de séance d'un travail qui n'en est pas.
 *
 * ⚠️ **Une dégressive et une série à l'échec restent DEHORS elles aussi**, et
 * c'est ce qui demande le plus d'explication. Le réflexe serait de les compter
 * ici — ce sont de vraies séries de ce mouvement-là. Mais le tableau de bord
 * les écarte de son tonnage, et « volume de séance » y désigne exactement la
 * même grandeur : deux définitions donneraient **2 000 kg dans la fiche et
 * 1 800 kg au tableau de bord pour la même séance**, sans que rien ne
 * l'explique. C'est le défaut que personne ne signale — on se dit qu'on avait
 * mal lu.
 *
 * ⚠️ Seule exception, et elle vient du tableau de bord lui aussi : le **maximum
 * estimé** lit `work` *et* `amrap`, parce qu'une série jusqu'à l'échec est le
 * meilleur intrant possible d'une estimation de maximum — c'est même ce qu'elle
 * mesure. Cf. `estimatedOneRepMaxOf`.
 */
function seriesDeTravail(passage: LastPassageDTO): readonly ActualSetDTO[] {
  return passage.sets.filter((set) => set.setKind === 'work');
}

/** La charge la plus lourde du passage. `null` au poids du corps. */
export function heaviestWeightOf(passage: LastPassageDTO): number | null {
  const charges = seriesDeTravail(passage)
    .map((set) => set.weightKg)
    .filter((kg): kg is number => kg !== null && kg > 0);
  return charges.length === 0 ? null : Math.max(...charges);
}

/**
 * Le maximum sur une répétition, **estimé** par Epley.
 *
 * 🔴 **La formule vient de `shared/oneRepMax.ts`**, celle-là même dont le
 * tableau de bord tire son classement (étape 9) : deux écritures auraient donné
 * deux maximums estimés pour le même exercice selon l'écran d'où on l'ouvre.
 * Plafond de 12 répétitions compris — au-delà, la formule cesse de vouloir dire
 * quelque chose et rend `null`.
 *
 * ⚠️ **`amrap` compte ici, et nulle part ailleurs** : une série jusqu'à l'échec
 * est le meilleur intrant possible d'une estimation de maximum. Elle sort du
 * volume parce qu'elle en fausserait le compte ; elle entre ici parce qu'elle
 * en est la matière. Même arbitrage qu'au tableau de bord.
 */
export function estimatedOneRepMaxOf(passage: LastPassageDTO): number | null {
  const estimations = passage.sets
    .filter((set) => set.setKind === 'work' || set.setKind === 'amrap')
    .map((set) => epley(set.weightKg, set.reps))
    .filter((value): value is number => value !== null);
  return estimations.length === 0 ? null : Math.max(...estimations);
}

/**
 * Le meilleur volume d'**une seule** série : `répétitions × charge`.
 *
 * ⚠️ **Distinct du volume de séance, et les deux sont utiles** : celui-ci dit
 * quelle a été la meilleure série du jour, celui-là ce qu'on a abattu en tout.
 * Monter le premier sans monter le second, c'est avoir tapé plus fort sur moins
 * de séries.
 */
export function bestSetVolumeOf(passage: LastPassageDTO): number | null {
  const volumes = seriesDeTravail(passage)
    .map((set) =>
      set.reps === null || set.weightKg === null || set.weightKg <= 0
        ? null
        : set.reps * set.weightKg,
    )
    .filter((value): value is number => value !== null && value > 0);
  return volumes.length === 0 ? null : Math.round(Math.max(...volumes));
}

/** Le volume de la séance entière : `Σ (répétitions × charge)`. */
export function sessionVolumeOf(passage: LastPassageDTO): number | null {
  const volumes = seriesDeTravail(passage)
    .map((set) =>
      set.reps === null || set.weightKg === null || set.weightKg <= 0
        ? 0
        : set.reps * set.weightKg,
    );
  const total = volumes.reduce((somme, value) => somme + value, 0);
  /*
   * ⚠️ **`null` et jamais zéro** sur un exercice au poids du corps : un zéro
   * tracerait une courbe à plat qui se lirait « je n'ai rien fait », alors
   * qu'il n'y a simplement pas de charge à multiplier. C'est la règle déjà
   * tenue par la courbe de charge.
   */
  return total === 0 ? null : Math.round(total);
}

/**
 * Le total de répétitions — ou de **secondes tenues** sur un exercice
 * isométrique.
 *
 * ⚠️ **L'unité suit la nature de l'exercice**, comme le faisait déjà
 * `volumeOf` : des répétitions pour un exercice qui s'en compte, des secondes
 * pour un gainage. On n'additionne jamais les deux — ce serait le genre de
 * nombre qui a l'air d'en être un.
 */
export function totalRepsOf(
  passage: LastPassageDTO,
): { value: number; unit: 'reps' | 's' } | null {
  const travail = seriesDeTravail(passage);

  const reps = travail.map((set) => set.reps).filter((value): value is number => value !== null);
  if (reps.length > 0) {
    return { value: reps.reduce((total, value) => total + value, 0), unit: 'reps' };
  }

  const secondes = travail
    .map((set) => set.durationSeconds)
    .filter((value): value is number => value !== null);
  if (secondes.length > 0) {
    return { value: secondes.reduce((total, value) => total + value, 0), unit: 's' };
  }
  return null;
}
